import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
// Mock data imports removed - using backend data only
import { bookingsAPI } from '../services/api';
import { formatCurrency, formatCurrencyCompact } from '../utils/currency';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Wallet,
  CreditCard,
  AlertCircle,
  Calendar,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  RefreshCw,
  Download,
  FileSpreadsheet,
  FileText,
  ChevronDown,
  Lock,
  Eye,
  EyeOff,
  Shield,
  Search,
  Receipt,
  Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import usePermissions from '../hooks/usePermissions';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const FinancialAnalytics = () => {
  const navigate = useNavigate();
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const { canExportFinancialAnalytics } = usePermissions();
  
  const [yearType, setYearType] = React.useState('calendar');
  const [selectedYear, setSelectedYear] = React.useState(currentYear);
  const [customStartDate, setCustomStartDate] = React.useState(new Date(currentYear, 0, 1).toISOString().split('T')[0]);
  const [customEndDate, setCustomEndDate] = React.useState(new Date().toISOString().split('T')[0]);
  const [bookings, setBookings] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const currentMonth = currentDate.getMonth();

  // Confidential Payment Report States
  const [isPaymentReportUnlocked, setIsPaymentReportUnlocked] = React.useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [reportFromDate, setReportFromDate] = React.useState('');
  const [reportToDate, setReportToDate] = React.useState('');
  const [paymentReportData, setPaymentReportData] = React.useState([]);
  const [isLoadingReport, setIsLoadingReport] = React.useState(false);
  const [selectedBookingPayments, setSelectedBookingPayments] = React.useState(null);
  const [bookingPaymentsDialogOpen, setBookingPaymentsDialogOpen] = React.useState(false);
  
  // Individual Booking Payment Search States
  const [bookingSearchName, setBookingSearchName] = React.useState('');
  const [bookingSearchDate, setBookingSearchDate] = React.useState('');
  const [filteredBookings, setFilteredBookings] = React.useState([]);
  const [selectedBookingForReport, setSelectedBookingForReport] = React.useState(null);
  const [showBookingSearchResults, setShowBookingSearchResults] = React.useState(false);

  // Function to load data - defined for refresh button
  const loadData = async () => {
    setIsLoading(true);
    try {
      const bookingsData = await bookingsAPI.getAll();
      const mappedBookings = bookingsData.map(booking => ({
        id: booking.id,
        customerName: booking.customerName,
        venueName: booking.venueName,
        eventDate: booking.eventDate,
        eventType: booking.eventType,
        status: booking.status?.toLowerCase() || 'confirmed',
        amount: booking.amount,
        advancePaid: booking.advancePaid || 0,
        advancePaymentMode: booking.advancePaymentMode || 'cash',
        advancePaymentNote: booking.advancePaymentNote || '',
        payments: booking.payments || [],
        phone: booking.phone,
        email: booking.email,
        createdAt: booking.createdAt
      }));
      setBookings(mappedBookings);
    } catch (error) {
      console.error('Failed to load bookings:', error);
      setBookings([]);
    }
    
    setIsLoading(false);
  };

  // Verify password for confidential report access
  const handlePasswordVerify = async () => {
    if (!password) {
      toast({ title: 'Error', description: 'Please enter your password', variant: 'destructive' });
      return;
    }

    setIsVerifying(true);
    try {
      // Verify password by attempting login with current user's email
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: currentUser.email, password: password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.access_token) {
        setIsPaymentReportUnlocked(true);
        setPasswordDialogOpen(false);
        setPassword('');
        toast({ title: '🔓 Access Granted', description: 'Confidential Payment Report unlocked' });
      } else {
        toast({ title: '❌ Access Denied', description: data.detail || 'Incorrect password', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: '❌ Access Denied', description: 'Incorrect password', variant: 'destructive' });
    }
    setIsVerifying(false);
  };

  // Generate payment report for date range
  const generatePaymentReport = () => {
    if (!reportFromDate || !reportToDate) {
      toast({ title: 'Error', description: 'Please select both From and To dates', variant: 'destructive' });
      return;
    }

    const fromDate = new Date(reportFromDate);
    const toDate = new Date(reportToDate);
    toDate.setHours(23, 59, 59, 999); // Include entire end day

    if (fromDate > toDate) {
      toast({ title: 'Error', description: 'From date cannot be after To date', variant: 'destructive' });
      return;
    }

    setIsLoadingReport(true);

    // Extract ALL payments from bookings within the date range
    // This includes: 1) Initial advance payments 2) Subsequent payments from payments array
    const paymentsInRange = [];
    
    bookings.forEach(booking => {
      // 1. Check if advance payment was made within the date range
      // Use booking createdAt as the advance payment date
      if (booking.advancePaid && booking.advancePaid > 0 && booking.createdAt) {
        const advancePaymentDate = new Date(booking.createdAt);
        if (advancePaymentDate >= fromDate && advancePaymentDate <= toDate) {
          paymentsInRange.push({
            paymentId: `advance-${booking.id}`,
            paymentDate: booking.createdAt,
            paymentAmount: booking.advancePaid,
            paymentMethod: booking.advancePaymentMode || 'cash',
            paymentNotes: booking.advancePaymentNote || 'Advance Payment',
            paymentType: 'Advance',
            bookingId: booking.id,
            customerName: booking.customerName,
            customerPhone: booking.phone,
            venueName: booking.venueName,
            eventType: booking.eventType,
            eventDate: booking.eventDate,
            totalBookingAmount: booking.amount,
            totalPayments: booking.payments,
            advancePaid: booking.advancePaid
          });
        }
      }

      // 2. Check all subsequent payments from the payments array
      if (booking.payments && booking.payments.length > 0) {
        booking.payments.forEach(payment => {
          const paymentDate = new Date(payment.date);
          if (paymentDate >= fromDate && paymentDate <= toDate) {
            paymentsInRange.push({
              paymentId: payment.id,
              paymentDate: payment.date,
              paymentAmount: payment.amount,
              paymentMethod: payment.method,
              paymentNotes: payment.notes || '-',
              paymentType: 'Payment',
              bookingId: booking.id,
              customerName: booking.customerName,
              customerPhone: booking.phone,
              venueName: booking.venueName,
              eventType: booking.eventType,
              eventDate: booking.eventDate,
              totalBookingAmount: booking.amount,
              totalPayments: booking.payments,
              advancePaid: booking.advancePaid
            });
          }
        });
      }
    });

    // Sort by payment date (most recent first)
    paymentsInRange.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));

    setPaymentReportData(paymentsInRange);
    setIsLoadingReport(false);

    if (paymentsInRange.length === 0) {
      toast({ title: 'No Data', description: 'No payments found in the selected date range', variant: 'default' });
    } else {
      toast({ title: 'Report Generated', description: `Found ${paymentsInRange.length} payments (including advance payments)`, variant: 'default' });
    }
  };

  // View all payments for a specific booking
  const viewBookingPayments = (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    if (booking) {
      setSelectedBookingPayments({
        ...booking,
        totalPaid: (booking.payments || []).reduce((sum, p) => sum + p.amount, 0),
        balance: booking.amount - (booking.payments || []).reduce((sum, p) => sum + p.amount, 0)
      });
      setBookingPaymentsDialogOpen(true);
    }
  };

  // Search bookings by name or event date
  const searchBookings = () => {
    if (!bookingSearchName && !bookingSearchDate) {
      setFilteredBookings([]);
      setShowBookingSearchResults(false);
      return;
    }

    let results = [...bookings];

    // Filter by customer name (case-insensitive)
    if (bookingSearchName) {
      const searchTerm = bookingSearchName.toLowerCase();
      results = results.filter(b => 
        b.customerName?.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by event date
    if (bookingSearchDate) {
      const searchDate = new Date(bookingSearchDate).toDateString();
      results = results.filter(b => {
        const eventDate = new Date(b.eventDate).toDateString();
        // Also check if it's a multi-day booking
        if (b.eventEndDate) {
          const startDate = new Date(b.eventDate);
          const endDate = new Date(b.eventEndDate);
          const searchDateObj = new Date(bookingSearchDate);
          return searchDateObj >= startDate && searchDateObj <= endDate;
        }
        return eventDate === searchDate;
      });
    }

    setFilteredBookings(results);
    setShowBookingSearchResults(true);
    
    if (results.length === 0) {
      toast({ title: 'No Results', description: 'No bookings found matching your search', variant: 'default' });
    }
  };

  // Select a booking for detailed payment report
  const selectBookingForReport = (booking) => {
    // Calculate total payments including advance
    const subsequentPayments = (booking.payments || []).reduce((sum, p) => sum + p.amount, 0);
    const totalPaid = (booking.advancePaid || 0) + subsequentPayments;
    const balance = booking.amount - totalPaid;

    setSelectedBookingForReport({
      ...booking,
      totalPaid,
      balance,
      subsequentPayments
    });
    setShowBookingSearchResults(false);
  };

  // Get all payments for the selected booking (for display and export)
  const getBookingPaymentDetails = (booking) => {
    if (!booking) return [];

    const payments = [];

    // Add advance payment if exists
    if (booking.advancePaid && booking.advancePaid > 0) {
      payments.push({
        type: 'Advance',
        date: booking.createdAt,
        amount: booking.advancePaid,
        mode: booking.advancePaymentMode || 'cash',
        notes: booking.advancePaymentNote || 'Initial Advance Payment'
      });
    }

    // Add all subsequent payments
    if (booking.payments && booking.payments.length > 0) {
      booking.payments.forEach(p => {
        payments.push({
          type: 'Payment',
          date: p.date,
          amount: p.amount,
          mode: p.method || 'cash',
          notes: p.notes || '-'
        });
      });
    }

    // Sort by date
    payments.sort((a, b) => new Date(a.date) - new Date(b.date));

    return payments;
  };

  // Export individual booking payment report to Excel
  const exportBookingPaymentExcel = () => {
    if (!selectedBookingForReport) return;

    const payments = getBookingPaymentDetails(selectedBookingForReport);
    
    // Create header info
    const headerData = [
      { 'BOOKING PAYMENT REPORT': '' },
      { 'Customer Name': selectedBookingForReport.customerName },
      { 'Phone': selectedBookingForReport.phone || '-' },
      { 'Venue': selectedBookingForReport.venueName },
      { 'Event Type': selectedBookingForReport.eventType },
      { 'Event Date': new Date(selectedBookingForReport.eventDate).toLocaleDateString('en-IN') + 
        (selectedBookingForReport.eventEndDate ? ' to ' + new Date(selectedBookingForReport.eventEndDate).toLocaleDateString('en-IN') : '') },
      { 'Total Booking Amount': selectedBookingForReport.amount },
      { 'Total Paid': selectedBookingForReport.totalPaid },
      { 'Balance Due': selectedBookingForReport.balance },
      { '': '' },
      { 'PAYMENT DETAILS': '' }
    ];

    // Create payment rows
    const paymentRows = payments.map((p, idx) => ({
      'S.No': idx + 1,
      'Date': new Date(p.date).toLocaleDateString('en-IN'),
      'Type': p.type,
      'Amount': p.amount,
      'Mode': p.mode?.toUpperCase() || 'CASH',
      'Notes': p.notes
    }));

    // Combine header and payment data
    const ws = XLSX.utils.json_to_sheet(headerData, { skipHeader: true });
    XLSX.utils.sheet_add_json(ws, paymentRows, { origin: 'A12' });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payment Report');
    XLSX.writeFile(wb, `Payment_Report_${selectedBookingForReport.customerName.replace(/\s+/g, '_')}.xlsx`);
    
    toast({ title: 'Exported', description: 'Individual booking payment report downloaded' });
  };

  // Export individual booking payment report to PDF
  const exportBookingPaymentPDF = () => {
    if (!selectedBookingForReport) return;

    const payments = getBookingPaymentDetails(selectedBookingForReport);
    const doc = new jsPDF();
    
    // Helper function
    const formatCurrencyForPDF = (amount) => 'Rs. ' + Number(amount || 0).toLocaleString('en-IN');

    // Title
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Booking Payment Report', 14, 15);
    
    // Booking Details
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Customer: ${selectedBookingForReport.customerName}`, 14, 25);
    doc.text(`Phone: ${selectedBookingForReport.phone || '-'}`, 14, 31);
    doc.text(`Venue: ${selectedBookingForReport.venueName}`, 14, 37);
    doc.text(`Event: ${selectedBookingForReport.eventType}`, 14, 43);
    
    const eventDateStr = new Date(selectedBookingForReport.eventDate).toLocaleDateString('en-IN') +
      (selectedBookingForReport.eventEndDate ? ' to ' + new Date(selectedBookingForReport.eventEndDate).toLocaleDateString('en-IN') : '');
    doc.text(`Event Date: ${eventDateStr}`, 14, 49);
    
    // Financial Summary
    doc.setFont(undefined, 'bold');
    doc.text('Financial Summary:', 14, 59);
    doc.setFont(undefined, 'normal');
    doc.text(`Total Booking Amount: ${formatCurrencyForPDF(selectedBookingForReport.amount)}`, 14, 65);
    doc.text(`Total Paid: ${formatCurrencyForPDF(selectedBookingForReport.totalPaid)}`, 14, 71);
    doc.text(`Balance Due: ${formatCurrencyForPDF(selectedBookingForReport.balance)}`, 14, 77);
    
    // Payment Table
    autoTable(doc, {
      startY: 85,
      head: [['S.No', 'Date', 'Type', 'Amount', 'Mode', 'Notes']],
      body: payments.map((p, idx) => [
        idx + 1,
        new Date(p.date).toLocaleDateString('en-IN'),
        p.type,
        formatCurrencyForPDF(p.amount),
        p.mode?.toUpperCase() || 'CASH',
        (p.notes?.substring(0, 40) || '-').replace(/₹/g, 'Rs.')
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [79, 70, 229] }
    });
    
    // Footer
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(8);
    doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 14, finalY);

    doc.save(`Payment_Report_${selectedBookingForReport.customerName.replace(/\s+/g, '_')}.pdf`);
    toast({ title: 'Exported', description: 'Individual booking payment report downloaded as PDF' });
  };

  // Export payment report to Excel
  const exportPaymentReportExcel = () => {
    if (paymentReportData.length === 0) {
      toast({ title: 'No Data', description: 'Generate a report first', variant: 'destructive' });
      return;
    }

    const exportData = paymentReportData.map(p => ({
      'Payment Date': new Date(p.paymentDate).toLocaleDateString('en-IN'),
      'Type': p.paymentType || 'Payment',
      'Amount': p.paymentAmount,
      'Payment Mode': p.paymentMethod?.toUpperCase() || 'CASH',
      'Payment Notes': p.paymentNotes,
      'Customer Name': p.customerName,
      'Phone': p.customerPhone,
      'Venue': p.venueName,
      'Event Type': p.eventType,
      'Event Date': new Date(p.eventDate).toLocaleDateString('en-IN'),
      'Total Booking Amount': p.totalBookingAmount
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payment Report');
    XLSX.writeFile(wb, `Payment_Report_${reportFromDate}_to_${reportToDate}.xlsx`);
    toast({ title: 'Exported', description: 'Payment report downloaded as Excel' });
  };

  // Export payment report to PDF
  const exportPaymentReportPDF = () => {
    if (paymentReportData.length === 0) {
      toast({ title: 'No Data', description: 'Generate a report first', variant: 'destructive' });
      return;
    }

    const doc = new jsPDF();
    
    // Helper function to format currency for PDF (without rupee symbol)
    const formatCurrencyForPDF = (amount) => {
      return 'Rs. ' + Number(amount || 0).toLocaleString('en-IN');
    };
    
    doc.setFontSize(16);
    doc.text('Confidential Payment Report', 14, 15);
    doc.setFontSize(10);
    doc.text('Period: ' + reportFromDate + ' to ' + reportToDate, 14, 22);
    doc.text('Generated: ' + new Date().toLocaleString('en-IN'), 14, 28);

    const totalAmount = paymentReportData.reduce((sum, p) => sum + p.paymentAmount, 0);
    doc.setFontSize(12);
    doc.text('Total Payments: ' + formatCurrencyForPDF(totalAmount), 14, 36);

    autoTable(doc, {
      startY: 42,
      head: [['Date', 'Type', 'Amount', 'Mode', 'Customer', 'Venue', 'Notes']],
      body: paymentReportData.map(p => [
        new Date(p.paymentDate).toLocaleDateString('en-IN'),
        p.paymentType || 'Payment',
        formatCurrencyForPDF(p.paymentAmount),
        p.paymentMethod?.toUpperCase() || 'CASH',
        p.customerName || '-',
        p.venueName || '-',
        (p.paymentNotes?.substring(0, 30) || '-').replace(/₹/g, 'Rs.')
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229] }
    });

    doc.save('Payment_Report_' + reportFromDate + '_to_' + reportToDate + '.pdf');
    toast({ title: 'Exported', description: 'Payment report downloaded as PDF' });
  };

  // Load data on mount
  React.useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Generate year options (current year ± 3 years)
  const yearOptions = [];
  for (let i = -3; i <= 3; i++) {
    yearOptions.push(currentYear + i);
  }
  
  // Calculate year range based on selection
  const getYearRange = () => {
    if (yearType === 'custom') {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      return {
        startMonth: startDate.getMonth(),
        startYear: startDate.getFullYear(),
        endMonth: endDate.getMonth(),
        endYear: endDate.getFullYear(),
        startDate: startDate,
        endDate: endDate,
        isCustom: true
      };
    } else if (yearType === 'financial') {
      return {
        startMonth: 3, // April
        startYear: selectedYear,
        endMonth: 2, // March
        endYear: selectedYear + 1,
        isCustom: false
      };
    } else {
      return {
        startMonth: 0, // January
        startYear: selectedYear,
        endMonth: 11, // December
        endYear: selectedYear,
        isCustom: false
      };
    }
  };

  const yearRange = getYearRange();
  
  // Get display year text
  const getYearDisplayText = () => {
    if (yearType === 'custom') {
      const formatShortDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      };
      return `${formatShortDate(customStartDate)} to ${formatShortDate(customEndDate)}`;
    } else if (yearType === 'financial') {
      return `FY ${selectedYear}-${(selectedYear + 1).toString().slice(-2)}`;
    } else {
      return selectedYear.toString();
    }
  };

  // Get report data
  const getReportData = () => {
    const headers = ['Customer Name', 'Venue', 'Event Date', 'Event Type', 'Status', 'Total Amount (₹)', 'Paid (₹)', 'Balance (₹)'];
    
    // Helper to truncate venue name to 25 characters
    const truncateVenueName = (name) => {
      if (!name) return '-';
      return name.length > 25 ? name.substring(0, 25) + '...' : name;
    };
    
    const data = thisYearBookings.map(booking => {
      const advance = Number(booking.advancePaid) || 0;
      const additionalPayments = (booking.payments || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);
      const totalPaid = advance + additionalPayments;
      const balance = Math.max(0, (Number(booking.amount) || 0) - totalPaid);
      return {
        'Customer Name': booking.customerName || '',
        'Venue': truncateVenueName(booking.venueName),
        'Event Date': new Date(booking.eventDate).toLocaleDateString(),
        'Event Type': booking.eventType || '',
        'Status': booking.status || '',
        'Total Amount (₹)': booking.amount || 0,
        'Paid (₹)': totalPaid,
        'Balance (₹)': balance
      };
    });

    const totalRevenueSum = thisYearBookings.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
    const totalPaidSum = thisYearBookings.reduce((sum, b) => {
      const advance = Number(b.advancePaid) || 0;
      const additionalPayments = (b.payments || []).reduce((pSum, p) => pSum + Number(p.amount || 0), 0);
      return sum + advance + additionalPayments;
    }, 0);
    const totalBalance = Math.max(0, totalRevenueSum - totalPaidSum);

    return { headers, data, totalRevenue: totalRevenueSum, totalPaid: totalPaidSum, totalBalance };
  };

  // Export as CSV
  const exportAsCSV = () => {
    try {
      const { headers, data, totalRevenue, totalPaid, totalBalance } = getReportData();
      const csvContent = [
        `Financial Analytics Report - ${getYearDisplayText()}`,
        `Generated: ${new Date().toLocaleString()}`,
        '',
        `Total Revenue: ${formatCurrency(totalRevenue)}`,
        `Total Collected: ${formatCurrency(totalPaid)}`,
        `Total Pending: ${formatCurrency(totalBalance)}`,
        '',
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h]}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `financial_analytics_${getYearDisplayText().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({ title: 'Export Successful', description: `CSV exported with ${data.length} bookings` });
    } catch (error) {
      console.error('CSV export failed:', error);
      toast({ title: 'Export Failed', description: 'Failed to export CSV', variant: 'destructive' });
    }
  };

  // Export as Excel
  const exportAsExcel = () => {
    try {
      const { headers, data, totalRevenue, totalPaid, totalBalance } = getReportData();
      
      const wb = XLSX.utils.book_new();
      
      const titleData = [
        [`Financial Analytics Report - ${getYearDisplayText()}`],
        [`Generated: ${new Date().toLocaleString()}`],
        [],
        [`Total Revenue: ₹${totalRevenue.toLocaleString()}`],
        [`Total Collected: ₹${totalPaid.toLocaleString()}`],
        [`Total Pending: ₹${totalBalance.toLocaleString()}`],
        [],
        headers
      ];
      
      data.forEach(row => {
        titleData.push(headers.map(h => row[h]));
      });

      const ws = XLSX.utils.aoa_to_sheet(titleData);
      ws['!cols'] = headers.map(() => ({ wch: 16 }));
      
      XLSX.utils.book_append_sheet(wb, ws, 'Financial Analytics');
      XLSX.writeFile(wb, `financial_analytics_${getYearDisplayText().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);

      toast({ title: 'Export Successful', description: `Excel exported with ${data.length} bookings` });
    } catch (error) {
      console.error('Excel export failed:', error);
      toast({ title: 'Export Failed', description: 'Failed to export Excel', variant: 'destructive' });
    }
  };

  // Export as PDF
  const exportAsPDF = () => {
    try {
      const { headers, data, totalRevenue, totalPaid, totalBalance } = getReportData();
      
      const doc = new jsPDF();
      
      // Helper function to format currency for PDF (without rupee symbol)
      const formatCurrencyForPDF = (amount) => {
        return 'Rs. ' + Number(amount || 0).toLocaleString('en-IN');
      };
      
      doc.setFontSize(18);
      doc.setTextColor(0, 128, 128);
      doc.text('Financial Analytics Report', 14, 20);
      
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(getYearDisplayText(), 14, 28);
      doc.text('Generated: ' + new Date().toLocaleString('en-IN'), 14, 35);
      
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text('Total Revenue: ' + formatCurrencyForPDF(totalRevenue), 14, 48);
      doc.text('Total Collected: ' + formatCurrencyForPDF(totalPaid), 14, 55);
      doc.text('Total Pending: ' + formatCurrencyForPDF(totalBalance), 14, 62);
      doc.text('Collection Rate: ' + (totalRevenue > 0 ? ((totalPaid / totalRevenue) * 100).toFixed(1) : 0) + '%', 14, 69);
      
      // Format data for PDF - replace rupee symbols
      const formattedData = data.map(row => {
        const formattedRow = {};
        headers.forEach(h => {
          let value = String(row[h] || '');
          // Replace rupee symbol with Rs.
          value = value.replace(/₹/g, 'Rs.');
          formattedRow[h] = value;
        });
        return formattedRow;
      });
      
      autoTable(doc, {
        head: [headers],
        body: formattedData.map(row => headers.map(h => row[h])),
        startY: 78,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [0, 128, 128], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 248, 255] }
      });

      doc.save('financial_analytics_' + getYearDisplayText().replace(/\s+/g, '_') + '_' + new Date().toISOString().split('T')[0] + '.pdf');

      toast({ title: 'Export Successful', description: 'PDF exported with ' + data.length + ' bookings' });
    } catch (error) {
      console.error('PDF export failed:', error);
      toast({ title: 'Export Failed', description: 'Failed to export PDF: ' + error.message, variant: 'destructive' });
    }
  };

  // Calculate selected year's bookings (or custom date range)
  const thisYearBookings = bookings.filter(b => {
    const bookingDate = new Date(b.eventDate);
    
    // For custom date range, use direct date comparison
    if (yearRange.isCustom) {
      const startDate = new Date(customStartDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999);
      return bookingDate >= startDate && bookingDate <= endDate;
    }
    
    // For calendar/financial year, use month-based comparison
    const bookingYear = bookingDate.getFullYear();
    const bookingMonth = bookingDate.getMonth();
    
    if (yearRange.startYear === yearRange.endYear) {
      return bookingYear === yearRange.startYear &&
             bookingMonth >= yearRange.startMonth &&
             bookingMonth <= yearRange.endMonth;
    } else {
      return (bookingYear === yearRange.startYear && bookingMonth >= yearRange.startMonth) ||
             (bookingYear === yearRange.endYear && bookingMonth <= yearRange.endMonth);
    }
  });

  // Calculate this month's bookings (current month only)
  const thisMonthBookings = bookings.filter(b => {
    const bookingDate = new Date(b.eventDate);
    return bookingDate.getMonth() === currentMonth && bookingDate.getFullYear() === currentYear;
  });

  // Calculate last month's bookings
  const lastMonthDate = new Date(currentYear, currentMonth - 1, 1);
  const lastMonthBookings = bookings.filter(b => {
    const bookingDate = new Date(b.eventDate);
    return bookingDate.getMonth() === lastMonthDate.getMonth() && 
           bookingDate.getFullYear() === lastMonthDate.getFullYear();
  });

  // Helper function to calculate total paid (advance + additional payments)
  const calculateTotalPaid = (booking) => {
    const advance = Number(booking.advancePaid) || 0;
    const additionalPayments = (booking.payments || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);
    return advance + additionalPayments;
  };

  // Financial calculations for selected year
  const totalRevenue = thisYearBookings.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
  const totalPaidAmount = thisYearBookings.reduce((sum, b) => sum + calculateTotalPaid(b), 0);
  const totalPendingPayments = thisYearBookings.reduce((sum, b) => {
    const totalPaid = calculateTotalPaid(b);
    const balance = Math.max(0, (Number(b.amount) || 0) - totalPaid);
    return sum + balance;
  }, 0);
  const confirmedRevenue = thisYearBookings.filter(b => b.status === 'confirmed').reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
  const blockedRevenue = thisYearBookings.filter(b => b.status === 'blocked').reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
  
  // This month revenue and payments (for comparison)
  const thisMonthRevenue = thisMonthBookings.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
  const thisMonthPaid = thisMonthBookings.reduce((sum, b) => sum + calculateTotalPaid(b), 0);
  const thisMonthPending = thisMonthBookings.reduce((sum, b) => {
    const paid = calculateTotalPaid(b);
    return sum + Math.max(0, (Number(b.amount) || 0) - paid);
  }, 0);
  
  const lastMonthRevenue = lastMonthBookings.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
  const lastMonthPaid = lastMonthBookings.reduce((sum, b) => sum + calculateTotalPaid(b), 0);
  const lastMonthPending = lastMonthBookings.reduce((sum, b) => {
    const paid = calculateTotalPaid(b);
    return sum + Math.max(0, (Number(b.amount) || 0) - paid);
  }, 0);

  // Calculate growth percentages (month over month)
  const revenueGrowth = lastMonthRevenue > 0 
    ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100).toFixed(1)
    : (thisMonthRevenue > 0 ? 100 : 0);
    
  const paidGrowth = lastMonthPaid > 0 
    ? ((thisMonthPaid - lastMonthPaid) / lastMonthPaid * 100).toFixed(1)
    : (thisMonthPaid > 0 ? 100 : 0);
    
  const pendingGrowth = lastMonthPending > 0 
    ? ((thisMonthPending - lastMonthPending) / lastMonthPending * 100).toFixed(1)
    : (thisMonthPending > 0 ? 100 : 0);

  // Calculate year-over-year growth for total revenue
  const lastYearBookings = bookings.filter(b => {
    const bookingDate = new Date(b.eventDate);
    return bookingDate.getFullYear() === selectedYear - 1;
  });
  const lastYearRevenue = lastYearBookings.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
  const yearOverYearGrowth = lastYearRevenue > 0 
    ? ((totalRevenue - lastYearRevenue) / lastYearRevenue * 100).toFixed(1)
    : (totalRevenue > 0 ? 100 : 0);

  // Monthly revenue trends (last 6 months)
  const monthlyRevenue = [];
  for (let i = 5; i >= 0; i--) {
    const targetDate = new Date(currentYear, currentMonth - i, 1);
    const monthBookings = bookings.filter(b => {
      const bookingDate = new Date(b.eventDate);
      return bookingDate.getMonth() === targetDate.getMonth() && 
             bookingDate.getFullYear() === targetDate.getFullYear();
    });
    const revenue = monthBookings.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
    const paid = monthBookings.reduce((sum, b) => sum + calculateTotalPaid(b), 0);
    monthlyRevenue.push({
      month: targetDate.toLocaleDateString('en-IN', { month: 'short' }),
      revenue: revenue,
      paid: paid,
      pending: Math.max(0, revenue - paid)
    });
  }

  // Payment collection rate (based on total paid, not just advance)
  const collectionRate = totalRevenue > 0 
    ? ((totalPaidAmount / totalRevenue) * 100).toFixed(1)
    : 0;

  const summaryStats = [
    {
      title: `Total Revenue (${getYearDisplayText()})`,
      value: formatCurrencyCompact(totalRevenue),
      icon: DollarSign,
      trend: lastYearRevenue > 0 ? `${yearOverYearGrowth > 0 ? '+' : ''}${yearOverYearGrowth}%` : null,
      trendLabel: lastYearRevenue > 0 ? 'vs last year' : null,
      trendUp: yearOverYearGrowth >= 0,
      color: 'from-teal-500 to-cyan-600',
      bgColor: 'bg-teal-50 dark:bg-teal-900/20',
      textColor: 'text-teal-600'
    },
    {
      title: 'This Month',
      value: formatCurrencyCompact(thisMonthRevenue),
      icon: Calendar,
      trend: `${revenueGrowth > 0 ? '+' : ''}${revenueGrowth}%`,
      trendLabel: 'vs last month',
      trendUp: revenueGrowth >= 0,
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-600'
    },
    {
      title: 'Total Received',
      value: formatCurrencyCompact(totalPaidAmount),
      icon: Wallet,
      trend: `${collectionRate}%`,
      trendLabel: 'collected',
      trendUp: true,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-600'
    },
    {
      title: 'Pending Payments',
      value: formatCurrencyCompact(totalPendingPayments),
      icon: AlertCircle,
      trend: `${(100 - collectionRate).toFixed(1)}%`,
      trendLabel: 'of total',
      trendUp: false,
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      textColor: 'text-orange-600'
    }
  ];

  // Venue-wise revenue (includes all payments, not just advance)
  const venueRevenue = {};
  bookings.forEach(b => {
    if (!venueRevenue[b.venueName]) {
      venueRevenue[b.venueName] = { total: 0, paid: 0, pending: 0 };
    }
    const totalPaid = calculateTotalPaid(b);
    venueRevenue[b.venueName].total += Number(b.amount) || 0;
    venueRevenue[b.venueName].paid += totalPaid;
    venueRevenue[b.venueName].pending += Math.max(0, (Number(b.amount) || 0) - totalPaid);
  });

  const topRevenueVenues = Object.entries(venueRevenue)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/dashboard')}
        className="flex items-center text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:text-foreground -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-foreground">Financial Analytics</h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1">Track revenue, payments, and financial performance</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button 
            onClick={loadData}
            variant="outline"
            className="flex items-center gap-2"
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Select value={yearType} onValueChange={setYearType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select Year Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="calendar">Calendar Year</SelectItem>
              <SelectItem value="financial">Financial Year</SelectItem>
              <SelectItem value="custom">Custom Date</SelectItem>
            </SelectContent>
          </Select>
          {yearType !== 'custom' ? (
            <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {yearType === 'financial' ? `FY ${year}-${(year + 1).toString().slice(-2)}` : year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-[140px]"
              />
              <span className="text-sm text-gray-500 dark:text-muted-foreground">to</span>
              <Input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-[140px]"
              />
            </div>
          )}
          {canExportFinancialAnalytics() && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700">
                  <Download className="w-4 h-4 mr-2" />
                  Export Report
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportAsCSV}>
                  <FileText className="w-4 h-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportAsExcel}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export as Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportAsPDF}>
                  <FileText className="w-4 h-4 mr-2 text-red-500" />
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {summaryStats.map((stat, index) => {
          const Icon = stat.icon;
          const TrendIcon = stat.trendUp ? ArrowUpRight : ArrowDownRight;
          return (
            <Card key={index} className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-muted-foreground">{stat.title}</p>
                  <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${stat.textColor}`} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-foreground mb-2">{stat.value}</p>
                {stat.trend && (
                  <div className="flex items-center space-x-1">
                    <TrendIcon className={`w-4 h-4 ${stat.trendUp ? 'text-green-600' : 'text-red-600'}`} />
                    <span className={`text-sm font-medium ${stat.trendUp ? 'text-green-600' : 'text-red-600'}`}>
                      {stat.trend}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-muted-foreground">{stat.trendLabel || 'vs last month'}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Revenue Breakdown & Collection Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Status */}
        <Card className="border-0 shadow-md">
          <CardHeader className="border-b bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30">
            <CardTitle className="flex items-center space-x-2 dark:text-foreground">
              <PieChart className="w-5 h-5 text-teal-600" />
              <span>Revenue by Booking Status</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200">
                <div>
                  <p className="font-medium text-gray-900 dark:text-foreground">Confirmed Revenue</p>
                  <p className="text-xs text-gray-600 dark:text-muted-foreground mt-1">From confirmed bookings</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-green-600">{formatCurrency(confirmedRevenue)}</p>
                  <p className="text-xs text-gray-600 dark:text-muted-foreground">{totalRevenue > 0 ? ((confirmedRevenue / totalRevenue) * 100).toFixed(1) : 0}%</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200">
                <div>
                  <p className="font-medium text-gray-900 dark:text-foreground">Blocked Dates Revenue</p>
                  <p className="text-xs text-gray-600 dark:text-muted-foreground mt-1">From blocked dates</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-yellow-600">{formatCurrency(blockedRevenue)}</p>
                  <p className="text-xs text-gray-600 dark:text-muted-foreground">{totalRevenue > 0 ? ((blockedRevenue / totalRevenue) * 100).toFixed(1) : 0}%</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Collection */}
        <Card className="border-0 shadow-md">
          <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30">
            <CardTitle className="flex items-center space-x-2 dark:text-foreground">
              <CreditCard className="w-5 h-5 text-purple-600" />
              <span>Payment Collection Status</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-foreground">Collection Rate</span>
                  <span className="text-2xl font-bold text-purple-600">{collectionRate}%</span>
                </div>
                <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-600 rounded-full"
                    style={{ width: `${collectionRate}%` }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
                  <p className="text-xs text-gray-600 dark:text-muted-foreground mb-1">Collected</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrencyCompact(totalPaidAmount)}</p>
                </div>
                <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                  <p className="text-xs text-gray-600 dark:text-muted-foreground mb-1">Pending</p>
                  <p className="text-lg font-bold text-orange-600">{formatCurrencyCompact(totalPendingPayments)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Revenue Trends */}
      <Card className="border-0 shadow-md">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
          <CardTitle className="flex items-center space-x-2 dark:text-foreground">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <span>Revenue Trends (Last 6 Months)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {monthlyRevenue.map((data, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-foreground w-16">{data.month}</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-foreground">{formatCurrency(data.revenue)}</span>
                </div>
                <div className="flex space-x-1">
                  <div className="flex-1 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-l-lg flex items-center justify-center"
                       style={{ width: `${data.revenue > 0 ? (data.paid / data.revenue) * 100 : 0}%` }}>
                    <span className="text-xs font-medium text-white">{formatCurrencyCompact(data.paid)}</span>
                  </div>
                  <div className="flex-1 h-8 bg-gradient-to-r from-orange-500 to-amber-600 rounded-r-lg flex items-center justify-center"
                       style={{ width: `${data.revenue > 0 ? (data.pending / data.revenue) * 100 : 0}%` }}>
                    <span className="text-xs font-medium text-white">{formatCurrencyCompact(data.pending)}</span>
                  </div>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-600 dark:text-muted-foreground">Collected: {data.revenue > 0 ? ((data.paid / data.revenue) * 100).toFixed(0) : 0}%</span>
                  <span className="text-xs text-gray-600 dark:text-muted-foreground">Pending: {data.revenue > 0 ? ((data.pending / data.revenue) * 100).toFixed(0) : 0}%</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Revenue Venues */}
      <Card className="border-0 shadow-md">
        <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30">
          <CardTitle className="flex items-center space-x-2 dark:text-foreground">
            <BarChart3 className="w-5 h-5 text-orange-600" />
            <span>Top Revenue Generating Venues</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {topRevenueVenues.map(([venue, data], index) => (
              <div key={index} className="p-4 rounded-lg bg-gray-50 dark:bg-secondary hover:bg-gray-100 dark:bg-secondary transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center text-white font-bold">
                      #{index + 1}
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-foreground">{venue}</span>
                  </div>
                  <span className="text-xl font-bold text-gray-900 dark:text-foreground">{formatCurrency(data.total)}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-2 rounded bg-green-50 dark:bg-green-900/20">
                    <p className="text-xs text-gray-600 dark:text-muted-foreground">Total Collected</p>
                    <p className="text-sm font-bold text-green-600">{formatCurrency(data.paid)}</p>
                  </div>
                  <div className="p-2 rounded bg-orange-50 dark:bg-orange-900/20">
                    <p className="text-xs text-gray-600 dark:text-muted-foreground">Pending Amount</p>
                    <p className="text-sm font-bold text-orange-600">{formatCurrency(data.pending)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Confidential Payment Report - Admin Only */}
      {currentUser?.role === 'admin' && (
        <Card className="border-2 border-red-200 dark:border-red-800 shadow-lg bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/30 dark:to-orange-900/30">
          <CardHeader className="border-b border-red-200 dark:border-red-800 bg-gradient-to-r from-red-100 to-orange-100 dark:from-red-900/40 dark:to-orange-900/40 p-3 sm:p-4 md:p-6">
            <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 dark:text-foreground">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                <span className="text-sm sm:text-base text-red-800 dark:text-red-400">Confidential Payment Report</span>
                <Badge variant="destructive" className="ml-1 sm:ml-2 text-[10px] sm:text-xs">Admin Only</Badge>
              </div>
              {!isPaymentReportUnlocked && (
                <Button 
                  onClick={() => setPasswordDialogOpen(true)}
                  className="bg-red-600 hover:bg-red-700 w-full sm:w-auto text-sm"
                  size="sm"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Unlock Report
                </Button>
              )}
              {isPaymentReportUnlocked && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setIsPaymentReportUnlocked(false);
                    setPaymentReportData([]);
                    setReportFromDate('');
                    setReportToDate('');
                  }}
                  className="border-red-300 text-red-600 hover:bg-red-50 dark:bg-red-900/20 w-full sm:w-auto text-sm"
                  size="sm"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Lock Report
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 md:p-6">
            {!isPaymentReportUnlocked ? (
              <div className="text-center py-8 sm:py-12">
                <Lock className="w-12 h-12 sm:w-16 sm:h-16 text-red-300 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-700 dark:text-foreground mb-2">Password Protected</h3>
                <p className="text-sm text-gray-500 dark:text-muted-foreground mb-4">Enter your login password to access payment details</p>
                <Button 
                  onClick={() => setPasswordDialogOpen(true)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Unlock with Password
                </Button>
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {/* Date Range Selection */}
                <div className="p-3 sm:p-4 bg-white dark:bg-card rounded-lg border border-gray-200 dark:border-border">
                  <h4 className="font-semibold text-gray-800 dark:text-foreground mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
                    <Calendar className="w-4 h-4 mr-2 text-red-600" />
                    Select Date Range
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                    <div>
                      <Label className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">From Date</Label>
                      <Input
                        type="date"
                        value={reportFromDate}
                        onChange={(e) => setReportFromDate(e.target.value)}
                        className="mt-1 h-9 sm:h-10 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">To Date</Label>
                      <Input
                        type="date"
                        value={reportToDate}
                        onChange={(e) => setReportToDate(e.target.value)}
                        className="mt-1 h-9 sm:h-10 text-sm"
                      />
                    </div>
                    <div className="flex items-end col-span-2 sm:col-span-1">
                      <Button 
                        onClick={generatePaymentReport}
                        disabled={isLoadingReport}
                        className="bg-red-600 hover:bg-red-700 w-full h-9 sm:h-10 text-xs sm:text-sm"
                      >
                        {isLoadingReport ? (
                          <><RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" /> Loading...</>
                        ) : (
                          <><Search className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Generate</>
                        )}
                      </Button>
                    </div>
                    <div className="flex items-end gap-2 col-span-2 sm:col-span-1">
                      <Button 
                        variant="outline"
                        onClick={exportPaymentReportExcel}
                        disabled={paymentReportData.length === 0}
                        className="flex-1 h-9 sm:h-10 text-xs sm:text-sm px-2"
                      >
                        <FileSpreadsheet className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="hidden sm:inline">Excel</span>
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={exportPaymentReportPDF}
                        disabled={paymentReportData.length === 0}
                        className="flex-1 h-9 sm:h-10 text-xs sm:text-sm px-2"
                      >
                        <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="hidden sm:inline">PDF</span>
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Report Summary */}
                {paymentReportData.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4">
                    <div className="p-2 sm:p-3 md:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200">
                      <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-muted-foreground">Total Payments</p>
                      <p className="text-sm sm:text-lg md:text-2xl font-bold text-green-600">
                        {formatCurrency(paymentReportData.reduce((sum, p) => sum + p.paymentAmount, 0))}
                      </p>
                    </div>
                    <div className="p-2 sm:p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                      <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-muted-foreground">No. of Payments</p>
                      <p className="text-sm sm:text-lg md:text-2xl font-bold text-blue-600">{paymentReportData.length}</p>
                    </div>
                    <div className="p-2 sm:p-3 md:p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200">
                      <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-muted-foreground">Unique Bookings</p>
                      <p className="text-sm sm:text-lg md:text-2xl font-bold text-purple-600">
                        {new Set(paymentReportData.map(p => p.bookingId)).size}
                      </p>
                    </div>
                  </div>
                )}

                {/* Payment Details - Mobile Cards / Desktop Table */}
                {paymentReportData.length > 0 && (
                  <>
                    {/* Mobile Card View */}
                    <div className="block md:hidden space-y-3">
                      {paymentReportData.map((payment, index) => (
                        <div key={index} className="bg-white dark:bg-card rounded-lg border border-gray-200 dark:border-border p-3 shadow-sm">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold text-gray-800 dark:text-foreground text-sm">{payment.customerName}</p>
                              <p className="text-xs text-gray-500 dark:text-muted-foreground">{payment.customerPhone}</p>
                            </div>
                            <p className="font-bold text-green-600 text-sm">
                              {formatCurrency(payment.paymentAmount)}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-1 mb-2">
                            <Badge 
                              className={`text-[10px] ${payment.paymentType === 'Advance' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700' : 'bg-green-100 dark:bg-green-900/30 text-green-700'}`}
                            >
                              {payment.paymentType || 'Payment'}
                            </Badge>
                            <Badge variant="outline" className="capitalize text-[10px]">
                              {payment.paymentMethod || 'Cash'}
                            </Badge>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-muted-foreground space-y-1">
                            <p><span className="text-gray-400 dark:text-muted-foreground">Date:</span> {new Date(payment.paymentDate).toLocaleDateString('en-IN')}</p>
                            <p><span className="text-gray-400 dark:text-muted-foreground">Event:</span> {payment.eventType} - {new Date(payment.eventDate).toLocaleDateString('en-IN')}</p>
                            <p><span className="text-gray-400 dark:text-muted-foreground">Venue:</span> {payment.venueName}</p>
                            {payment.paymentNotes && <p><span className="text-gray-400 dark:text-muted-foreground">Notes:</span> {payment.paymentNotes}</p>}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewBookingPayments(payment.bookingId)}
                            className="w-full mt-3 text-xs h-8"
                          >
                            <Receipt className="w-3 h-3 mr-1" />
                            View All Payments
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block bg-white dark:bg-card rounded-lg border border-gray-200 dark:border-border overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-secondary">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-muted-foreground">Date</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-muted-foreground">Type</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-muted-foreground">Amount</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-muted-foreground">Mode</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-muted-foreground">Customer</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-muted-foreground">Event</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-muted-foreground">Venue</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-muted-foreground">Notes</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-muted-foreground">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {paymentReportData.map((payment, index) => (
                              <tr key={index} className="hover:bg-gray-50 dark:bg-secondary">
                                <td className="px-3 py-2 text-sm whitespace-nowrap">
                                  {new Date(payment.paymentDate).toLocaleDateString('en-IN')}
                                </td>
                                <td className="px-3 py-2">
                                  <Badge 
                                    className={`text-[10px] ${payment.paymentType === 'Advance' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700' : 'bg-green-100 dark:bg-green-900/30 text-green-700'}`}
                                  >
                                    {payment.paymentType || 'Payment'}
                                  </Badge>
                                </td>
                                <td className="px-3 py-2 text-sm font-semibold text-green-600 whitespace-nowrap">
                                  {formatCurrency(payment.paymentAmount)}
                                </td>
                                <td className="px-3 py-2">
                                  <Badge variant="outline" className="capitalize text-[10px]">
                                    {payment.paymentMethod || 'Cash'}
                                  </Badge>
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <div className="max-w-[120px]">
                                    <p className="truncate">{payment.customerName}</p>
                                    <p className="text-xs text-gray-500 dark:text-muted-foreground">{payment.customerPhone}</p>
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <div className="max-w-[100px]">
                                    <p className="truncate">{payment.eventType}</p>
                                    <p className="text-xs text-gray-500 dark:text-muted-foreground">
                                      {new Date(payment.eventDate).toLocaleDateString('en-IN')}
                                    </p>
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-sm max-w-[100px] truncate">{payment.venueName}</td>
                                <td className="px-3 py-2 text-sm text-gray-600 dark:text-muted-foreground max-w-[100px] truncate">
                                  {payment.paymentNotes || '-'}
                                </td>
                                <td className="px-3 py-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => viewBookingPayments(payment.bookingId)}
                                    className="text-xs h-7 px-2"
                                  >
                                    <Receipt className="w-3 h-3 mr-1" />
                                    All
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}

                {paymentReportData.length === 0 && reportFromDate && reportToDate && (
                  <div className="text-center py-6 sm:py-8 text-gray-500 dark:text-muted-foreground">
                    <Receipt className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No payments found in the selected date range</p>
                  </div>
                )}

                {/* Individual Booking Payment Search */}
                <div className="p-3 sm:p-4 bg-white dark:bg-card rounded-lg border border-blue-200 mt-4 sm:mt-6">
                  <h4 className="font-semibold text-gray-800 dark:text-foreground mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
                    <Receipt className="w-4 h-4 mr-2 text-blue-600" />
                    Individual Booking Payment Record
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-muted-foreground mb-3 sm:mb-4">
                    Search for a specific booking to view and download its complete payment history
                  </p>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-4">
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">Customer Name</Label>
                      <Input
                        type="text"
                        placeholder="Enter name..."
                        value={bookingSearchName}
                        onChange={(e) => setBookingSearchName(e.target.value)}
                        className="mt-1 h-9 sm:h-10 text-sm"
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">Event Date</Label>
                      <Input
                        type="date"
                        value={bookingSearchDate}
                        onChange={(e) => setBookingSearchDate(e.target.value)}
                        className="mt-1 h-9 sm:h-10 text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button 
                        onClick={searchBookings}
                        className="bg-blue-600 hover:bg-blue-700 w-full h-9 sm:h-10 text-xs sm:text-sm"
                      >
                        <Search className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        Search
                      </Button>
                    </div>
                    <div className="flex items-end">
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setBookingSearchName('');
                          setBookingSearchDate('');
                          setFilteredBookings([]);
                          setShowBookingSearchResults(false);
                          setSelectedBookingForReport(null);
                        }}
                        className="w-full h-9 sm:h-10 text-xs sm:text-sm"
                      >
                        Clear
                      </Button>
                    </div>
                  </div>

                  {/* Search Results */}
                  {showBookingSearchResults && filteredBookings.length > 0 && (
                    <div className="bg-gray-50 dark:bg-secondary rounded-lg p-4 mb-4">
                      <h5 className="text-sm font-semibold text-gray-700 dark:text-foreground mb-3">
                        Search Results ({filteredBookings.length} bookings found)
                      </h5>
                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {filteredBookings.map((booking) => (
                          <div 
                            key={booking.id}
                            onClick={() => selectBookingForReport(booking)}
                            className="p-3 bg-white dark:bg-card rounded border border-gray-200 dark:border-border hover:border-blue-400 hover:bg-blue-50 dark:bg-blue-900/20 cursor-pointer transition-colors"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold text-gray-800 dark:text-foreground text-sm">{booking.customerName}</p>
                                <p className="text-xs sm:text-sm text-gray-500 dark:text-muted-foreground">
                                  {booking.eventType} • {booking.venueName}
                                </p>
                                <p className="text-[10px] sm:text-xs text-gray-400 dark:text-muted-foreground">
                                  Event: {new Date(booking.eventDate).toLocaleDateString('en-IN')}
                                  {booking.eventEndDate && ` to ${new Date(booking.eventEndDate).toLocaleDateString('en-IN')}`}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-green-600 text-sm">{formatCurrency(booking.amount)}</p>
                                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground">
                                  {(booking.payments?.length || 0) + (booking.advancePaid > 0 ? 1 : 0)} payment(s)
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Selected Booking Payment Details */}
                  {selectedBookingForReport && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 sm:p-4 border border-blue-200">
                      {/* Booking Header */}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-blue-200">
                        <div>
                          <h5 className="text-base sm:text-lg font-bold text-gray-800 dark:text-foreground">{selectedBookingForReport.customerName}</h5>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">{selectedBookingForReport.phone}</p>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">
                            {selectedBookingForReport.eventType} at {selectedBookingForReport.venueName}
                          </p>
                          <p className="text-[10px] sm:text-sm text-gray-500 dark:text-muted-foreground">
                            Event: {new Date(selectedBookingForReport.eventDate).toLocaleDateString('en-IN')}
                            {selectedBookingForReport.eventEndDate && ` to ${new Date(selectedBookingForReport.eventEndDate).toLocaleDateString('en-IN')}`}
                          </p>
                        </div>
                        <div className="text-left sm:text-right">
                          <p className="text-xs sm:text-sm text-gray-500 dark:text-muted-foreground">Total Booking Amount</p>
                          <p className="text-lg sm:text-xl font-bold text-gray-800 dark:text-foreground">{formatCurrency(selectedBookingForReport.amount)}</p>
                        </div>
                      </div>

                      {/* Payment Summary */}
                      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4">
                        <div className="bg-white dark:bg-card p-2 sm:p-3 rounded-lg text-center">
                          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground">Total Paid</p>
                          <p className="text-sm sm:text-lg font-bold text-green-600">{formatCurrency(selectedBookingForReport.totalPaid)}</p>
                        </div>
                        <div className="bg-white dark:bg-card p-2 sm:p-3 rounded-lg text-center">
                          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground">Balance Due</p>
                          <p className={`text-sm sm:text-lg font-bold ${selectedBookingForReport.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatCurrency(selectedBookingForReport.balance)}
                          </p>
                        </div>
                        <div className="bg-white dark:bg-card p-2 sm:p-3 rounded-lg text-center">
                          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground">Payments</p>
                          <p className="text-sm sm:text-lg font-bold text-blue-600">
                            {(selectedBookingForReport.payments?.length || 0) + (selectedBookingForReport.advancePaid > 0 ? 1 : 0)}
                          </p>
                        </div>
                      </div>

                      {/* Payment Details - Mobile Cards / Desktop Table */}
                      {/* Mobile Card View */}
                      <div className="block sm:hidden space-y-2 mb-3">
                        {getBookingPaymentDetails(selectedBookingForReport).map((payment, idx) => (
                          <div key={idx} className="bg-white dark:bg-card rounded-lg p-3 border border-gray-100 dark:border-border">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 dark:text-muted-foreground">#{idx + 1}</span>
                                <Badge 
                                  className={`text-[10px] ${payment.type === 'Advance' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700' : 'bg-green-100 dark:bg-green-900/30 text-green-700'}`}
                                >
                                  {payment.type}
                                </Badge>
                              </div>
                              <p className="text-sm font-bold text-green-600">{formatCurrency(payment.amount)}</p>
                            </div>
                            <div className="text-xs text-gray-600 dark:text-muted-foreground space-y-1">
                              <p><span className="text-gray-400 dark:text-muted-foreground">Date:</span> {new Date(payment.date).toLocaleDateString('en-IN')}</p>
                              <p><span className="text-gray-400 dark:text-muted-foreground">Mode:</span> <span className="capitalize">{payment.mode}</span></p>
                              {payment.notes && <p><span className="text-gray-400 dark:text-muted-foreground">Notes:</span> {payment.notes}</p>}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Desktop Table View */}
                      <div className="hidden sm:block bg-white dark:bg-card rounded-lg overflow-hidden mb-4">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-100 dark:bg-secondary">
                              <tr>
                                <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-muted-foreground">#</th>
                                <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-muted-foreground">Date</th>
                                <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-muted-foreground">Type</th>
                                <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-muted-foreground">Amount</th>
                                <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-muted-foreground">Mode</th>
                                <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-muted-foreground">Notes</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {getBookingPaymentDetails(selectedBookingForReport).map((payment, idx) => (
                                <tr key={idx} className="hover:bg-gray-50 dark:bg-secondary">
                                  <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm">{idx + 1}</td>
                                  <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm whitespace-nowrap">{new Date(payment.date).toLocaleDateString('en-IN')}</td>
                                  <td className="px-2 sm:px-3 py-2">
                                    <Badge 
                                      className={`text-[10px] ${payment.type === 'Advance' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700' : 'bg-green-100 dark:bg-green-900/30 text-green-700'}`}
                                    >
                                      {payment.type}
                                    </Badge>
                                  </td>
                                  <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-semibold text-green-600 whitespace-nowrap">{formatCurrency(payment.amount)}</td>
                                  <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm capitalize">{payment.mode}</td>
                                  <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-600 dark:text-muted-foreground max-w-[100px] truncate">{payment.notes || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Export Buttons */}
                      <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                        <Button 
                          variant="outline"
                          onClick={() => setSelectedBookingForReport(null)}
                          className="order-3 sm:order-1 text-xs sm:text-sm h-9"
                        >
                          Close
                        </Button>
                        <Button 
                          onClick={exportBookingPaymentExcel}
                          className="order-1 sm:order-2 bg-green-600 hover:bg-green-700 text-xs sm:text-sm h-9"
                        >
                          <FileSpreadsheet className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          Excel
                        </Button>
                        <Button 
                          onClick={exportBookingPaymentPDF}
                          className="order-2 sm:order-3 bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm h-9"
                        >
                          <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Password Verification Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Shield className="w-5 h-5 mr-2 text-red-600" />
              Verify Identity
            </DialogTitle>
            <DialogDescription>
              Enter your login password to access the confidential payment report
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your login password"
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordVerify()}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handlePasswordVerify} 
                disabled={isVerifying}
                className="bg-red-600 hover:bg-red-700"
              >
                {isVerifying ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                ) : (
                  <><Lock className="w-4 h-4 mr-2" /> Verify & Unlock</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking All Payments Dialog */}
      <Dialog open={bookingPaymentsDialogOpen} onOpenChange={setBookingPaymentsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Receipt className="w-5 h-5 mr-2 text-green-600" />
              All Payments for Booking
            </DialogTitle>
            {selectedBookingPayments && (
              <DialogDescription>
                {selectedBookingPayments.customerName} - {selectedBookingPayments.venueName}
              </DialogDescription>
            )}
          </DialogHeader>
          {selectedBookingPayments && (
            <div className="space-y-4 mt-4">
              {/* Booking Summary */}
              <div className="p-4 bg-gray-50 dark:bg-secondary rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-muted-foreground">Customer</p>
                    <p className="font-semibold">{selectedBookingPayments.customerName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-muted-foreground">Venue</p>
                    <p className="font-semibold">{selectedBookingPayments.venueName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-muted-foreground">Event Type</p>
                    <p className="font-semibold">{selectedBookingPayments.eventType}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-muted-foreground">Event Date</p>
                    <p className="font-semibold">
                      {new Date(selectedBookingPayments.eventDate).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <p className="text-xs text-gray-600 dark:text-muted-foreground">Total Amount</p>
                  <p className="text-lg font-bold text-blue-600">
                    {formatCurrency(selectedBookingPayments.amount)}
                  </p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-xs text-gray-600 dark:text-muted-foreground">Total Paid</p>
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(selectedBookingPayments.totalPaid)}
                  </p>
                </div>
                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-center">
                  <p className="text-xs text-gray-600 dark:text-muted-foreground">Balance Due</p>
                  <p className="text-lg font-bold text-orange-600">
                    {formatCurrency(selectedBookingPayments.balance)}
                  </p>
                </div>
              </div>

              {/* Payment Details */}
              <div>
                <h4 className="font-semibold mb-3">Payment History</h4>
                {/* Show advance payment if exists */}
                {selectedBookingPayments.advancePaid > 0 && (
                  <div className="mb-2 p-3 border rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-green-700 dark:text-green-400">
                            {formatCurrency(selectedBookingPayments.advancePaid)}
                          </span>
                          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 border-green-300 text-xs">
                            ADVANCE
                          </Badge>
                          {selectedBookingPayments.advancePaymentMode && (
                            <Badge variant="outline" className="capitalize text-xs">
                              {selectedBookingPayments.advancePaymentMode}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1">
                          {selectedBookingPayments.createdAt ? new Date(selectedBookingPayments.createdAt).toLocaleString('en-IN') : 'At Booking'}
                        </p>
                      </div>
                    </div>
                    {selectedBookingPayments.advancePaymentNote && (
                      <div className="mt-2 p-2 bg-green-100/50 dark:bg-green-900/30 rounded text-sm text-gray-600 dark:text-muted-foreground">
                        <span className="font-medium">Notes:</span> {selectedBookingPayments.advancePaymentNote}
                      </div>
                    )}
                  </div>
                )}
                {/* Show subsequent payments */}
                {selectedBookingPayments.payments && selectedBookingPayments.payments.length > 0 ? (
                  <div className="space-y-2">
                    {selectedBookingPayments.payments.map((payment, index) => (
                      <div key={index} className="p-3 border rounded-lg bg-white dark:bg-card">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-green-600">
                                {formatCurrency(payment.amount)}
                              </span>
                              <Badge variant="outline" className="capitalize text-xs">
                                {payment.method || 'Cash'}
                              </Badge>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1">
                              {new Date(payment.date).toLocaleString('en-IN')}
                            </p>
                          </div>
                        </div>
                        {payment.notes && (
                          <div className="mt-2 p-2 bg-gray-50 dark:bg-secondary rounded text-sm text-gray-600 dark:text-muted-foreground">
                            <span className="font-medium">Notes:</span> {payment.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : !selectedBookingPayments.advancePaid && (
                  <p className="text-gray-500 dark:text-muted-foreground text-center py-4">No payments recorded yet</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialAnalytics;
